import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import '@/App.css';

import { AuthProvider } from './lib/auth';
import { UnreadMessagesProvider } from './lib/unreadMessages';
import { I18nProvider } from './i18n';
import { TopBar } from './components/TopBar';
import { MobileBottomNav } from './components/MobileBottomNav';
import { CookieConsent } from './components/CookieConsent';
import { AnalyticsTracker } from './components/AnalyticsTracker';
import { api } from './lib/api';

import HomePage from './pages/HomePage';
import ShortsPage from './pages/ShortsPage';
import WatchPage from './pages/WatchPage';
import LoginPage from './pages/LoginPage';
import UploadPage from './pages/UploadPage';
import ProfilePage from './pages/ProfilePage';
import CategoriesPage from './pages/CategoriesPage';
import SearchPage from './pages/SearchPage';
import AdminPage from './pages/AdminPage';
import EmbedPage from './pages/EmbedPage';
import UserPublicPage from './pages/UserPublicPage';
import LivePage from './pages/LivePage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SeriesPage from './pages/SeriesPage';
import FriendsPage from './pages/FriendsPage';

function AppShell() {
    const location = useLocation();
    const isShorts = location.pathname.startsWith('/shorts');
    // Global AdSense loader: the admin can paste the <script> tag provided by
    // Google AdSense in Admin -> Anuncis; it is injected once into <head>.
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get('/ads/platform-config');
                const code = (r.data?.adsense_script || '').trim();
                if (!code || cancelled || document.querySelector('script[data-adsense-script="1"]')) return;
                const already = Array.from(document.querySelectorAll('script[src]')).some((s) => s.src.includes('adsbygoogle.js'));
                if (already) return;
                const wrapper = document.createElement('div');
                wrapper.innerHTML = code;
                wrapper.querySelectorAll('script').forEach((node) => {
                    const script = document.createElement('script');
                    for (const attr of node.attributes) script.setAttribute(attr.name, attr.value);
                    script.setAttribute('data-adsense-script', '1');
                    script.text = node.textContent;
                    document.head.appendChild(script);
                });
            } catch {}
        })();
        return () => { cancelled = true; };
    }, []);
    // Captura el canal d'origen (?ref=canal) perquè el registre l'atribueixi
    // a l'agent de màrqueting; es desa per usar-lo en crear el compte.
    useEffect(() => {
        const ref = new URLSearchParams(location.search).get('ref');
        if (ref) {
            const clean = ref.toLowerCase().replace(/[^a-z0-9_-]/g, '').slice(0, 40);
            if (clean) {
                try { localStorage.setItem('alb_ref', clean); } catch {}
            }
        }
    }, [location.search]);
    return (
        <div className="App">
            <AnalyticsTracker />
            <TopBar />
            <Routes>
                <Route path="/" element={<HomePage />} />
                <Route path="/shorts" element={<ShortsPage />} />
                <Route path="/video/:videoId" element={<WatchPage />} />
                <Route path="/series/:seriesId" element={<SeriesPage />} />
                <Route path="/entrar" element={<LoginPage />} />
                <Route path="/pujar" element={<UploadPage />} />
                <Route path="/perfil" element={<ProfilePage />} />
                <Route path="/categories" element={<CategoriesPage />} />
                <Route path="/categories/:name" element={<CategoriesPage />} />
                <Route path="/cerca" element={<SearchPage />} />
                <Route path="/live/:liveId" element={<LivePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/usuari/:userId" element={<UserPublicPage />} />
                <Route path="/amics" element={<FriendsPage />} />
                <Route path="/admin" element={<AdminPage />} />
                <Route path="/embed/:videoId" element={<EmbedPage />} />
            </Routes>
            {!isShorts && <MobileBottomNav />}
            {isShorts && <MobileBottomNav />}
            <CookieConsent />
        </div>
    );
}

function App() {
    return (
        <I18nProvider>
            <AuthProvider>
                <UnreadMessagesProvider>
                    <BrowserRouter>
                        <AppShell />
                        <Toaster richColors position="top-center" theme="dark" />
                    </BrowserRouter>
                </UnreadMessagesProvider>
            </AuthProvider>
        </I18nProvider>
    );
}

export default App;
